// Refresh saved schedules before exporting; preserve the visible unsaved draft.
async function requestJourneyPdf({ api, boutique, date, log, dirty, isCurrent }) {
  const snapshot = dirty ? log : (await api.get(`/daily-logs/${boutique}/${date}`, {
    params: { refresh: Date.now() }, headers: { "Cache-Control": "no-cache" },
  })).data;
  const assertCurrent = () => {
    if (!isCurrent()) throw new Error("The selected day changed. Download again from the day you want.");
  };
  assertCurrent();
  let response;
  try {
    response = await api.post('/daily-logs/export.pdf', {
      boutique_code: boutique, date, roster: snapshot.roster || [], rooms: snapshot.rooms || {},
      side_duties: snapshot.side_duties || [], google_reviews_goal: snapshot.google_reviews_goal ?? null,
    }, { responseType: 'blob' });
  } catch (error) {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      let detail;
      try { detail = JSON.parse(await body.text()).detail; } catch { /* Use the request error. */ }
      if (typeof detail === 'string') throw new Error(detail);
    }
    throw error;
  }
  assertCurrent();
  if (!response.data?.size || !response.data.type.includes('application/pdf')) {
    throw new Error('The server did not return a PDF. Please try again.');
  }
  return response.data;
}

export async function downloadJourneyPdf(options) {
  const url = URL.createObjectURL(await requestJourneyPdf(options));
  const link = document.createElement('a');
  link.href = url;
  link.download = `Journey-${options.boutique}-${options.date}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Open synchronously from the click so Safari does not block an async popup.
// Both actions use the same PDF, including its page size and grouped form.
export async function printJourneyPdf(options) {
  const preview = window.open('', '_blank');
  if (!preview) throw new Error('Allow the print window to open, then try Print again.');
  preview.document.title = 'Journey print preview';
  preview.document.body.textContent = 'Preparing your print preview…';
  let url;
  try {
    url = URL.createObjectURL(await requestJourneyPdf(options));
    if (preview.closed) { URL.revokeObjectURL(url); return; }
    // Safari's embedded PDF frame can remain blank. Use its native, top-level
    // PDF viewer, which exposes the browser's normal Print command.
    preview.location.replace(url);
    preview.focus();
    // Keep the PDF available for another print until this preview closes.
    const cleanup = setInterval(() => {
      if (preview.closed) { URL.revokeObjectURL(url); clearInterval(cleanup); }
    }, 1000);
  } catch (error) {
    preview.close();
    if (url) URL.revokeObjectURL(url);
    throw error;
  }
}
